import { ApiError, api } from '@invisoutlet/shared/api';
import type { AddOnKey, EntityPreference, IsoString } from '@invisoutlet/shared/types';
import { formatDate } from '@invisoutlet/shared/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { InternalLayout } from '../components/internal-layout.js';
import { ADD_ON_KEYS, ADD_ON_SHORT_LABELS } from '../constants/entity.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminEntityListItem {
  id: string;
  name: string;
  buildingCount: number;
  deviceCount: number;
  userCount: number;
  addOns: Record<AddOnKey, boolean>;
  preference: EntityPreference;
  createdAt: IsoString;
  active: boolean;
}

interface AdminEntitiesResponse {
  data: AdminEntityListItem[];
  meta: { total: number; limit: number; offset: number };
}

interface CreateEntityBody {
  id: string;
  name: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getActiveAddOnKeys(addOns: Record<AddOnKey, boolean>): AddOnKey[] {
  return ADD_ON_KEYS.filter((k) => addOns[k]);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AddOnBadges({ addOns }: { addOns: Record<AddOnKey, boolean> | undefined }) {
  const active = getActiveAddOnKeys(addOns ?? ({} as Record<AddOnKey, boolean>));
  if (active.length === 0) {
    return <span className="text-xs text-text-secondary">None</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {active.map((key) => (
        <span
          key={key}
          className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success"
        >
          {ADD_ON_SHORT_LABELS[key]}
        </span>
      ))}
    </div>
  );
}

interface CreateEntityModalProps {
  onClose: () => void;
  onCreated: () => void;
}

// Must match the regex in functions/src/routes/admin.ts POST /entities
const ENTITY_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const ENTITY_ID_MAX_LENGTH = 128;

function CreateEntityModal({ onClose, onCreated }: CreateEntityModalProps) {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateEntityBody) =>
      api.post<AdminEntityListItem>('/api/admin/entities', body),
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create entity.');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Entity name is required.');
      return;
    }
    if (!id.trim()) {
      setError('Entity ID is required.');
      return;
    }
    if (!ENTITY_ID_PATTERN.test(id.trim())) {
      setError(
        'Entity ID must start with a letter and contain only letters, numbers, hyphens, or underscores.',
      );
      return;
    }
    if (id.trim().length > ENTITY_ID_MAX_LENGTH) {
      setError(`Entity ID must be ${ENTITY_ID_MAX_LENGTH} characters or fewer.`);
      return;
    }
    mutation.mutate({ id: id.trim(), name: name.trim() });
  }

  return (
    /* Backdrop */
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-entity-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-xl border border-border bg-bg-primary shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 id="create-entity-title" className="text-base font-semibold text-text-primary">
            Create Entity
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="entity-name" className="text-sm font-medium text-text-primary">
                Entity Name{' '}
                <span aria-hidden="true" className="text-danger">
                  *
                </span>
              </label>
              <input
                id="entity-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Property Management"
                className="rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="entity-id" className="text-sm font-medium text-text-primary">
                Entity ID{' '}
                <span aria-hidden="true" className="text-danger">
                  *
                </span>
              </label>
              <input
                id="entity-id"
                type="text"
                required
                aria-describedby="entity-id-hint"
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="acmePropertyMgmt"
                className="rounded-lg border border-border bg-bg-surface px-3 py-2 font-mono text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <p id="entity-id-hint" className="text-xs text-text-secondary">
                Used as the Firestore document ID. Letters, numbers, hyphens, underscores only.
                Cannot be changed later.
              </p>
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? 'Creating…' : 'Create Entity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeactivateConfirmProps {
  entityName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function DeactivateConfirm({ entityName, onConfirm, onCancel, isPending }: DeactivateConfirmProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deactivate-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} aria-hidden="true" />
      <div className="relative w-full max-w-sm rounded-xl border border-border bg-bg-primary p-6 shadow-xl">
        <h2 id="deactivate-title" className="text-base font-semibold text-text-primary">
          Deactivate Entity
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          Are you sure you want to deactivate{' '}
          <span className="font-medium text-text-primary">{entityName}</span>? This will prevent
          client users from accessing the portal.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-danger/50 disabled:opacity-50"
          >
            {isPending ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EntitiesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deactivatingEntity, setDeactivatingEntity] = useState<AdminEntityListItem | null>(null);

  const params = new URLSearchParams();
  if (search) params.set('search', search);
  params.set('limit', '50');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-entities', search],
    queryFn: () =>
      api.get<AdminEntitiesResponse['data']>(`/api/admin/entities?${params.toString()}`),
  });

  const deactivateMutation = useMutation({
    mutationFn: (entityId: string) => api.delete(`/api/admin/entities/${entityId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-entities'] });
      setDeactivatingEntity(null);
    },
  });

  const entities: AdminEntityListItem[] = (data?.data as AdminEntityListItem[] | undefined) ?? [];
  const total: number = data?.meta?.total ?? entities.length;

  return (
    <InternalLayout title="Entities">
      <div className="flex flex-col gap-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Entities</h2>
            <p className="mt-0.5 text-sm text-text-secondary">
              {isLoading ? 'Loading…' : `${total} total`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Entity
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            placeholder="Search entities…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-sm rounded-lg border border-border bg-bg-surface py-2 pl-9 pr-4 text-sm text-text-primary outline-none transition-colors placeholder:text-text-secondary focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-text-secondary text-sm">
            Loading entities…
          </div>
        ) : error ? (
          <div role="alert" className="rounded-lg bg-danger/10 px-4 py-3 text-sm text-danger">
            {error instanceof ApiError
              ? error.message
              : 'Failed to load entities. Please try again.'}
          </div>
        ) : entities.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-bg-surface py-20 text-center">
            <svg
              className="mb-3 h-10 w-10 text-text-secondary"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.25"
              aria-hidden="true"
            >
              <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M8 10v2M8 14v2M12 10v2M12 14v2M16 10v2M16 14v2" />
            </svg>
            <p className="text-sm font-medium text-text-primary">No entities found</p>
            <p className="mt-1 text-xs text-text-secondary">
              {search ? 'Try a different search term.' : 'Create your first entity to get started.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-bg-surface">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Entity
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Buildings
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Devices
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Users
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Add-ons
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Created
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-secondary"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-bg-primary">
                {entities.map((entity) => (
                  <tr key={entity.id} className="transition-colors hover:bg-bg-surface">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/entities/${entity.id}`}
                          className="font-medium text-text-primary hover:text-accent hover:underline"
                        >
                          {entity.name}
                        </Link>
                        {entity.active === false && (
                          <span className="rounded-full bg-danger/10 px-1.5 py-0.5 text-xs font-medium text-danger">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary">
                      {entity.buildingCount ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary">
                      {entity.deviceCount ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary">
                      {entity.userCount ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <AddOnBadges addOns={entity.addOns} />
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {formatDate(entity.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/entities/${entity.id}`}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent/10 focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          View
                        </Link>
                        <button
                          type="button"
                          onClick={() => setDeactivatingEntity(entity)}
                          disabled={entity.active === false}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-danger transition-colors hover:bg-danger/10 focus:outline-none focus:ring-2 focus:ring-danger disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateEntityModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => void queryClient.invalidateQueries({ queryKey: ['admin-entities'] })}
        />
      )}

      {deactivatingEntity && (
        <DeactivateConfirm
          entityName={deactivatingEntity.name}
          isPending={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate(deactivatingEntity.id)}
          onCancel={() => setDeactivatingEntity(null)}
        />
      )}
    </InternalLayout>
  );
}
